import React, { useCallback, useEffect, useRef, useState } from 'react';

// NOW — the queue, ported whole from legacy/app.js (2026-08-23, phase 2 of
// the React port). Portals into #page-now. The .now-* dress is in
// missions.css; the capture row (.bucket-add / .ba-*), the drag ghost and the
// context menu keep their legacy/style.css rules.
//
// THREE BLOCKS (owner call 2026-10-01). Today answers "what do I do now?" —
// late rows at its top, done rows sunk to its bottom. Under it, one quiet "up
// next" — what is dated for THIS WEEK, a handful at a time — with "→ today" on
// each row. Under THAT, "in general": everything else you hold, undated work
// and anything dated past this week, in one scrolling block so the page is the
// whole list again and not only its near edge. Sending undated work off to its
// mission (owner call 2026-09-22) meant the only way to see what you were
// carrying was to visit each mission in turn. It opens CLOSED, carrying its
// count — fifty rows standing open under the day would answer a question the
// page was not asked — and the browser remembers whichever way you leave it.
// All three blocks obey the people filter. The engine's buckets are untouched (the home ledger counts with
// them); this page shows three of them.
//
// THE DATE LAW STAYS THE ENGINE'S: buckets come from window.bucketForItem /
// isHidden / isDone — the same functions the home ledger counts with — and
// the engine's loadNow() remains the ONE fetch (it keeps _nowItems for the
// agent context and announces missions:now for this page). Every mutation
// here calls window.loadNow() so both worlds stay in step.

const TODAY_BUCKETS = new Set(['overdue', 'today']);
const NEXT_BUCKETS = ['week'];
const GENERAL_BUCKETS = ['general'];
const NEXT_SHOWN = 5;
// What the page is SHOWING — the chip counts have to agree with it, and the
// tail is a disclosure, so what it holds counts only while it is open.
const NEAR_BUCKETS = new Set([...TODAY_BUCKETS, ...NEXT_BUCKETS]);
const PAGE_BUCKETS = new Set([...NEAR_BUCKETS, ...GENERAL_BUCKETS]);
const UNASSIGNED = '__none__';
const TAIL_KEY = 'missions_new:nowTailOpen';
const VALID_DROP_BUCKETS = new Set(['today', 'week', 'general']);

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const inGroup = () => {
  try { return !!localStorage.getItem('missions_new:activeGroupId'); } catch { return false; }
};

// Map a bucket key → the target_date for a drop/menu move. Mirrors
// bucketForItem() in reverse; null = clear the date ("in general").
function bucketToTargetDate(bucketKey) {
  const today = startOfToday();
  if (bucketKey === 'today') return localYmd(today);
  if (bucketKey === 'week') {
    const d = new Date(today);
    const dow = d.getDay();
    d.setDate(d.getDate() + (dow === 0 ? 7 : 7 - dow));
    return localYmd(d);
  }
  return null;
}

// A move keeps a placement's wall-clock time and duration — only the day
// changes; "in general" clears the placement entirely (legacy law).
async function moveItemToBucket(item, bucketKey) {
  const targetDate = bucketToTargetDate(bucketKey);
  try {
    if (item.scheduled_at) {
      if (bucketKey === 'general') {
        await window.putJson(`/data/schedule/${item.id}`, { scheduled_at: null, scheduled_end_at: null });
      } else {
        const clock = String(item.scheduled_at).slice(11, 19) || '09:00:00';
        const durationMs = item.scheduled_end_at
          ? Math.max(new Date(item.scheduled_end_at) - new Date(item.scheduled_at), 0)
          : 45 * 60 * 1000;
        const [yy, mo, dd] = targetDate.split('-').map(Number);
        const [hh, mi, se] = clock.split(':').map(Number);
        const startLocal = new Date(yy, mo - 1, dd, hh, mi, se || 0);
        const endLocal = new Date(startLocal.getTime() + durationMs);
        const p2 = (n) => String(n).padStart(2, '0');
        const hms = (dt) => `${p2(dt.getHours())}:${p2(dt.getMinutes())}:${p2(dt.getSeconds())}`;
        await window.putJson(`/data/schedule/${item.id}`, {
          scheduled_at: window.withLocalOffset(`${localYmd(startLocal)}T${hms(startLocal)}`),
          scheduled_end_at: window.withLocalOffset(`${localYmd(endLocal)}T${hms(endLocal)}`),
        });
      }
    }
    await window.putJson(`/api/items/${item.id}`, { value_json: { target_date: targetDate } });
    await window.loadNow();
  } catch (e) {
    alert(`Couldn't move — ${e.message}`);
  }
}

async function setItemTargetDate(itemId, targetDate) {
  try {
    await window.putJson(`/api/items/${itemId}`, { value_json: { target_date: targetDate } });
    await window.loadNow();
  } catch (e) {
    alert(`Couldn't move — ${e.message}`);
  }
}

// ONE TOKEN ON THE RIGHT, and only what the block does not already say. In
// today the block says "today", so a row shows its time if it has one, or
// nothing — INCLUDING a late one (owner call 2026-10-01): "50d late" beside a
// row was a scolding, not information, and the order already puts late work
// first. In up next and in general a row says its day, or its date.
function whenLabelFor(item) {
  const today = startOfToday();
  if (window.isDone?.(item)) return '';
  if (item.kind === 'habit') {
    const canon = window.canonStatus?.(item.status);
    if (canon === 'paused') return 'paused';
    return item.cadence || '';
  }
  const dateStr = item.scheduled_at || item.target_date;
  if (!dateStr) return '';
  const due = window.parseYmdLocal?.(dateStr) || new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / 86400000);
  if (diff <= 0) {
    // a placement at midnight is a day, not a time
    const t = item.scheduled_at ? String(item.scheduled_at).slice(11, 16) : '';
    return t && t !== '00:00' ? t : '';
  }
  if (diff <= 6) return due.toLocaleDateString(undefined, { weekday: 'short' });
  return due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
const dayLine = () => new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });

// ── The drag — legacy startNowDrag, verbatim mechanics (ghost, 2D nearest
// target, edge auto-scroll), operating on the real DOM the same way ─────────
function startNowDrag(downEvent, item, row, onDropped, suppressClick) {
  const rect = row.getBoundingClientRect();
  const offsetX = downEvent.clientX - rect.left;
  const offsetY = downEvent.clientY - rect.top;
  let ghost = null;
  let started = false;
  let activeDrop = null;
  const captureEl = row;
  try { captureEl.setPointerCapture(downEvent.pointerId); } catch {}

  const ensureStarted = () => {
    if (started) return;
    started = true;
    ghost = row.cloneNode(true);
    ghost.classList.add('ol-ghost', 'now-ghost');
    ghost.style.width = rect.width + 'px';
    ghost.style.height = rect.height + 'px';
    document.body.appendChild(ghost);
    row.classList.add('dragging');
    document.body.classList.add('ol-dragging');
  };

  const clearDrop = () => {
    document.querySelectorAll('.queue-section.drop-active').forEach((s) => s.classList.remove('drop-active'));
    activeDrop = null;
  };

  const updateDropTarget = (clientX, clientY) => {
    const targets = Array.from(document.querySelectorAll('.queue-section[data-bucket]'))
      .filter((t) => VALID_DROP_BUCKETS.has(t.dataset.bucket));
    let target = null;
    for (const t of targets) {
      const r = t.getBoundingClientRect();
      if (clientY >= r.top && clientY <= r.bottom && clientX >= r.left && clientX <= r.right) { target = t; break; }
    }
    if (!target) {
      let best = null; let bestDist = Infinity;
      for (const t of targets) {
        const r = t.getBoundingClientRect();
        const dx = Math.max(r.left - clientX, 0, clientX - r.right);
        const dy = Math.max(r.top - clientY, 0, clientY - r.bottom);
        const dist = Math.hypot(dx, dy);
        if (dist > 140) continue;
        if (dist < bestDist) { bestDist = dist; best = t; }
      }
      if (best) target = best;
    }
    clearDrop();
    if (target) { target.classList.add('drop-active'); activeDrop = target.dataset.bucket; }
  };

  const EDGE_ZONE = 84;
  const MAX_STEP = 20;
  let lastPoint = null;
  let scrollRaf = null;
  const scrollableUnder = (x, y) => {
    let el = document.elementFromPoint(x, y);
    while (el && el !== document.body && el !== document.documentElement) {
      if (el.scrollHeight > el.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(el).overflowY)) return el;
      el = el.parentElement;
    }
    return null;
  };
  const stepScroll = () => {
    scrollRaf = null;
    if (!started || !lastPoint) return;
    const { x, y } = lastPoint;
    const h = window.innerHeight;
    let dy = 0;
    if (y < EDGE_ZONE) dy = -MAX_STEP * (1 - y / EDGE_ZONE);
    else if (y > h - EDGE_ZONE) dy = MAX_STEP * (1 - (h - y) / EDGE_ZONE);
    if (dy) {
      const region = scrollableUnder(x, y);
      const before = region ? region.scrollTop : window.scrollY;
      if (region) region.scrollTop += dy; else window.scrollBy(0, dy);
      const after = region ? region.scrollTop : window.scrollY;
      if (after !== before) updateDropTarget(x, y);
    }
    queueScroll();
  };
  const queueScroll = () => { if (scrollRaf === null && started) scrollRaf = requestAnimationFrame(stepScroll); };

  const onMove = (e) => {
    if (!started) {
      const dx = e.clientX - downEvent.clientX;
      const dy = e.clientY - downEvent.clientY;
      if (dx * dx + dy * dy < 9) return;
      ensureStarted();
    }
    ghost.style.left = (e.clientX - offsetX) + 'px';
    ghost.style.top = (e.clientY - offsetY) + 'px';
    lastPoint = { x: e.clientX, y: e.clientY };
    updateDropTarget(e.clientX, e.clientY);
    queueScroll();
  };
  const cleanup = () => {
    captureEl.removeEventListener('pointermove', onMove);
    captureEl.removeEventListener('pointerup', onUp);
    captureEl.removeEventListener('pointercancel', onUp);
    try { captureEl.releasePointerCapture(downEvent.pointerId); } catch {}
    if (scrollRaf !== null) { cancelAnimationFrame(scrollRaf); scrollRaf = null; }
    lastPoint = null;
    if (ghost) ghost.remove();
    row.classList.remove('dragging');
    document.body.classList.remove('ol-dragging');
    clearDrop();
  };
  const onUp = async () => {
    const wasDrag = started;
    const droppedBucket = activeDrop;
    cleanup();
    if (!wasDrag) return;
    suppressClick();
    if (droppedBucket && droppedBucket !== item.bucket) await onDropped(droppedBucket);
  };
  captureEl.addEventListener('pointermove', onMove);
  captureEl.addEventListener('pointerup', onUp);
  captureEl.addEventListener('pointercancel', onUp);
}

// ── In-bucket capture (the legacy bucket-add, as a component) ────────────────
function BucketAdd({ bucketKey, who, onAdded }) {
  const LABELS = {
    today: ['add to today', 'what needs to happen today?'],
    week: ['add to up next', 'what needs to happen this week?'],
    general: ['add to the list', 'what needs to happen, whenever?'],
  };
  const [label, placeholder] = LABELS[bucketKey] || LABELS.general;
  const [active, setActive] = useState(false);
  const [title, setTitle] = useState('');
  const [mission, setMission] = useState(null);
  const [missions, setMissions] = useState(null);
  const [popOpen, setPopOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // outside-click collapses (and closes the popover) — the legacy doc handler
  useEffect(() => {
    if (!active) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setActive(false); setPopOpen(false); setTitle('');
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [active]);

  const open = async (e) => {
    e.stopPropagation();
    setActive(true);
    if (!mission) {
      const list = await window.getNowMissions();
      const last = window.getLastUsedMissionId?.();
      const m = (list || []).find((x) => x.id === last) || null;
      if (m) setMission(m);
    }
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const submit = async () => {
    const t = title.trim();
    if (!t || !mission) return;
    setBusy(true);
    // Whose list you're capturing into: exactly one person filtered → theirs.
    const assignees = (inGroup() && who.size === 1 && ![...who][0].startsWith('__'))
      ? [...who] : null;
    try {
      await window.postJson(`/api/items/${mission.id}/children`, {
        kind: 'action',
        title: t,
        target_date: bucketKey === 'general' ? null : bucketToTargetDate(bucketKey),
        ...(assignees ? { assignee_ids: assignees } : {}),
      });
      window.setLastUsedMissionId?.(mission.id);
      setActive(false); setPopOpen(false); setTitle('');
      onAdded(!assignees);
      await window.loadNow();
    } catch (err) {
      alert(`Couldn't add — ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={rootRef}
         className={`bucket-add${active ? ' active' : ''}${title.trim() ? ' has-text' : ''}`}
         data-bucket={bucketKey}>
      <div className="ba-empty" onClick={open}>{label}</div>
      <div className="ba-active">
        <input ref={inputRef} className="ba-input" placeholder={placeholder} value={title}
               onChange={(e) => setTitle(e.target.value)}
               onKeyDown={(e) => {
                 if (e.key === 'Escape') { e.preventDefault(); setActive(false); setPopOpen(false); setTitle(''); }
                 else if (e.key === 'Enter') { e.preventDefault(); if (title.trim() && mission && !busy) submit(); }
               }} />
        <button type="button" className={`ba-mission-pill${mission ? '' : ' unset'}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!popOpen && missions === null) setMissions((await window.getNowMissions()) || []);
                  setPopOpen((v) => !v);
                }}>
          <span className="ba-mission-label">{mission ? mission.name : 'pick mission'}</span>
          <span className="qa-caret">▾</span>
          {popOpen && (
            <div className="ba-mission-popover">
              {missions && missions.length ? missions.map((m) => (
                <button key={m.id} type="button"
                        className={`ba-mission-opt${mission?.id === m.id ? ' on' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setMission(m); setPopOpen(false); inputRef.current?.focus(); }}>
                  <span>{m.name}</span>
                  {m.id === window.getLastUsedMissionId?.() && <span className="ba-last">last used</span>}
                </button>
              )) : <div className="ba-empty-state">no missions yet — create one first</div>}
            </div>
          )}
        </button>
        <button type="button" className="ba-add-btn" title="Add" disabled={!(title.trim() && mission) || busy}
                onClick={(e) => { e.stopPropagation(); submit(); }}>↵</button>
      </div>
    </div>
  );
}

// ── The row ──────────────────────────────────────────────────────────────────
function QueueItem({ item, index, inNext, soloFilter, suppressRef, onCtx }) {
  const done = window.isDone?.(item) || false;
  const when = whenLabelFor(item);
  const isVirtual = !!item.virtual;
  const rowRef = useRef(null);
  const avatars = soloFilter ? '' : (window.assigneeAvatarsHtml?.(item) || '');
  const canPull = inNext && item.kind === 'action' && !isVirtual;

  // THE TICK IS A MOMENT: the box fills and draws its check, the strike draws
  // across the title (CSS, on .done), and only then does the list re-sort so
  // the row sinks to the bottom. The write goes out at once; the reload waits.
  const toggle = async (e) => {
    e.stopPropagation();
    const box = e.currentTarget;
    const row = rowRef.current;
    const wasDone = box.classList.contains('done');
    box.classList.toggle('done');
    row?.classList.toggle('done');
    const settle = () => new Promise((r) => setTimeout(r, wasDone ? 0 : 650));
    const undo = () => { box.classList.toggle('done'); row?.classList.toggle('done'); };
    if (item.virtual && item.parent_habit_id) {
      try {
        await window.postJson(`/api/habits/${item.parent_habit_id}/log`, { status: wasDone ? 'open' : 'completed' });
        await settle();
        await window.loadNow();
      } catch (err) {
        undo();
        alert(`Update failed: ${err.message}`);
      }
      return;
    }
    const canon = window.canonStatus?.(item.status);
    const newStatus = item.kind === 'habit'
      ? (canon === 'paused' ? 'in_progress' : 'paused')
      : (wasDone ? 'open' : 'completed');
    try {
      await window.putJson(`/api/items/${item.id}`, { value_json: { status: newStatus } });
      await settle();
      await window.loadNow();
    } catch (err) {
      undo();
      alert(`Update failed: ${err.message}`);
    }
  };

  return (
    <div ref={rowRef}
         className={`queue-item ${done ? 'done' : ''} now-item${isVirtual ? ' virtual' : ''}`}
         style={{ '--i': Math.min(index, 12) }}
         data-id={item.id} data-kind={item.kind}
         onClick={() => {
           if (suppressRef.current) { suppressRef.current = false; return; }
           if (item.id && String(item.id).startsWith('virtual:')) return;
           window.openSheet?.(item.id);
         }}
         onContextMenu={(e) => {
           if (item.id && String(item.id).startsWith('virtual:')) return;
           e.preventDefault();
           onCtx(e.clientX, e.clientY, item);
         }}
         onPointerDown={(e) => {
           if (e.button !== 0) return;
           if (e.target.classList.contains('check')) return;
           if (item.kind !== 'action') return; // habits aren't datable
           if (item.id && String(item.id).startsWith('virtual:')) return;
           startNowDrag(e, item, rowRef.current,
             (bucket) => moveItemToBucket(item, bucket),
             () => { suppressRef.current = true; });
         }}>
      <button type="button" className={`check ${done ? 'done' : ''}`} aria-label={done ? 'Mark open' : 'Mark done'} onClick={toggle}>
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 6.5l2.6 2.6L10 3.5" /></svg>
      </button>
      <span className="qi-title"><span className="qi-title-text">{item.title || 'untitled'}</span></span>
      <div className="qi-meta">
        {item.mission_title && <span className="qi-mission" title={item.mission_title}>{item.mission_title}</span>}
        {avatars && <span dangerouslySetInnerHTML={{ __html: avatars }} />}
        {canPull && (
          <button type="button" className="qi-pull"
                  onClick={(e) => { e.stopPropagation(); moveItemToBucket(item, 'today'); }}>→ today</button>
        )}
        <span className="qi-when">{when}</span>
      </div>
    </div>
  );
}

// ── Right-click menu (the legacy now-ctxmenu, as state) ──────────────────────
function CtxMenu({ menu, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('click', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', onDoc); document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let x = menu.x, y = menu.y;
    if (x + el.offsetWidth > window.innerWidth - 8) x = window.innerWidth - el.offsetWidth - 8;
    if (y + el.offsetHeight > window.innerHeight - 8) y = window.innerHeight - el.offsetHeight - 8;
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
  }, [menu]);
  const { item } = menu;
  const isAction = item.kind === 'action';
  const done = window.isDone?.(item) || false;
  const act = async (fn) => { onClose(); await fn(); };
  return (
    <div ref={ref} className="now-ctxmenu">
      {isAction && (
        <div className="ctx-group">
          <div className="ctx-label">Set due</div>
          <button className="ctx-item" onClick={() => act(() => moveItemToBucket(item, 'today'))}>Today</button>
          <button className="ctx-item" onClick={() => act(() => {
            const d = startOfToday(); d.setDate(d.getDate() + 1);
            return setItemTargetDate(item.id, localYmd(d));
          })}>Tomorrow</button>
          <button className="ctx-item" onClick={() => act(() => moveItemToBucket(item, 'week'))}>This week</button>
          <button className="ctx-item" onClick={() => act(() => moveItemToBucket(item, 'general'))}>Whenever</button>
        </div>
      )}
      <div className="ctx-group">
        {isAction && (
          <button className="ctx-item" onClick={() => act(async () => {
            try {
              await window.putJson(`/api/items/${item.id}`, { value_json: { status: done ? 'open' : 'completed' } });
              await window.loadNow();
            } catch (err) { alert(`Couldn't update — ${err.message}`); }
          })}>{done ? 'Mark open' : 'Mark complete'}</button>
        )}
        <button className="ctx-item danger" onClick={() => act(async () => {
          if (!confirm(`Delete "${item.title || 'untitled'}"?`)) return;
          try {
            const res = await fetch(window.maybeScopeUrl(`/api/items/${item.id}`), { method: 'DELETE', credentials: 'same-origin' });
            if (!res.ok && res.status !== 204) throw new Error(res.statusText);
            await window.loadNow();
          } catch (err) { alert(`Couldn't delete — ${err.message}`); }
        })}>Delete</button>
      </div>
    </div>
  );
}

export default function Now({ active, userId }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  // Who to show (group mode): empty = everyone. Seeded to YOU on first load.
  const [who, setWho] = useState(() => new Set());
  const seededRef = useRef(false);
  const [ctx, setCtx] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  // The tail's own disclosure, remembered per browser: one click, not one a day.
  const [tailOpen, setTailOpen] = useState(() => {
    try { return localStorage.getItem(TAIL_KEY) === '1'; } catch { return false; }
  });
  const shownBuckets = tailOpen ? PAGE_BUCKETS : NEAR_BUCKETS;
  const suppressRef = useRef(false);

  useEffect(() => {
    const onNow = (e) => { setItems(e.detail.items); setError(null); };
    const onErr = (e) => setError(e.detail);
    document.addEventListener('missions:now', onNow);
    document.addEventListener('missions:now-error', onErr);
    return () => {
      document.removeEventListener('missions:now', onNow);
      document.removeEventListener('missions:now-error', onErr);
    };
  }, []);
  useEffect(() => {
    if (!active) return;
    window.loadNow?.();
  }, [active]);
  // the carried banner is still the engine's (it owns the dismissal key)
  useEffect(() => {
    if (active && items) window.loadCarriedBanner?.();
  }, [active, items]);

  const ownersOf = useCallback((it) => {
    const ids = window.assigneeIds?.(it) || [];
    return ids.length ? ids : [UNASSIGNED];
  }, []);
  // Seed the filter to YOU once the items are in — but only if any of them
  // are yours. Seeding blind hid the whole list behind a chip that was not
  // even rendered (the roster comes from the group's members + the rows'
  // owners, and the member list can lag /auth/status); an empty page with
  // "13 open" beside it. No work of yours = everyone's, which is honest.
  useEffect(() => {
    if (!active || !items || seededRef.current) return;
    seededRef.current = true;
    const mineHere = items.some((it) => shownBuckets.has(it.bucket) && ownersOf(it).includes(userId));
    if (inGroup() && userId && mineHere) setWho(new Set([userId]));
  }, [active, items, userId, ownersOf, shownBuckets]);

  if (!active && items === null) return null;
  if (error) return <div className="missions-empty">Couldn't load now — {error}</div>;
  if (items === null) return <div className="loading">loading…</div>;

  const visible = (!inGroup() || who.size === 0)
    ? items
    : items.filter((it) => ownersOf(it).some((uid) => who.has(uid)));

  // Roster chips: open-work counts over the rows THE PAGE IS SHOWING — the near
  // blocks, and the tail only while the tail stands open. A chip's number is
  // the list you get when you press it, which is the whole use of it; counting
  // what the payload carries but the page does not draw put "50" beside a name
  // whose list was four rows long. So the numbers move when the tail does, and
  // the tail's own head says what is folded away behind it. You lead, chips
  // with nothing open drop out unless selected (or yours).
  let chips = null;
  if (inGroup()) {
    const open = items.filter((it) => shownBuckets.has(it.bucket) && !window.isDone?.(it));
    const ids = (window.activeGroupMembers?.() || []).map((m) => m.user_id);
    if (userId && !ids.includes(userId)) ids.push(userId); // you are always on your own roster
    for (const it of items) for (const uid of ownersOf(it)) if (uid !== UNASSIGNED && !ids.includes(uid)) ids.push(uid);
    const ordered = [...ids.filter((u) => u === userId), ...ids.filter((u) => u !== userId)];
    if (items.some((it) => ownersOf(it).includes(UNASSIGNED))) ordered.push(UNASSIGNED);
    const rows = ordered.map((uid) => ({
      id: uid,
      name: uid === UNASSIGNED ? 'unassigned' : (window.resolveMemberName?.(uid) || 'member'),
      count: open.filter((it) => ownersOf(it).includes(uid)).length,
    })).filter((r) => r.count > 0 || who.has(r.id) || r.id === userId);
    chips = (
      <div className="now-toggles-row">
        <div className="now-people">
          {rows.map((r) => (
            <button key={r.id} className={`now-chip${who.has(r.id) ? ' on' : ''}`}
                    onClick={() => setWho((cur) => {
                      const next = new Set(cur);
                      if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                      return next;
                    })}>
              {r.name}<span className="c">{r.count}</span>
            </button>
          ))}
          <button className={`now-chip all${who.size === 0 ? ' on' : ''}`} onClick={() => setWho(new Set())}>
            Everyone<span className="c">{open.length}</span>
          </button>
        </div>
      </div>
    );
  }

  const onAdded = (unassigned) => {
    // A capture no filter can see would vanish — widen by the unassigned chip.
    if (unassigned && inGroup() && who.size && !who.has(UNASSIGNED)) {
      setWho((cur) => new Set([...cur, UNASSIGNED]));
    }
  };

  // TODAY: late first, then today's open rows, then the done ones sunk to the
  // bottom. UP NEXT: this week's dated rows, a handful shown. IN GENERAL: what
  // carries a date past this week, soonest first, then the undated — and the
  // done sunk under both, the way Today does it.
  const isDone = (it) => !!window.isDone?.(it);
  const dateOf = (it) => String(it.scheduled_at || it.target_date || '').slice(0, 10) || '9999';
  const byDate = (a, b) => (dateOf(a) < dateOf(b) ? -1 : dateOf(a) > dateOf(b) ? 1 : 0);
  const todayRows = visible.filter((it) => TODAY_BUCKETS.has(it.bucket));
  const todayOpen = [
    ...todayRows.filter((it) => !isDone(it) && it.bucket === 'overdue'),
    ...todayRows.filter((it) => !isDone(it) && it.bucket !== 'overdue'),
  ];
  const todayDone = todayRows.filter(isDone);
  const nextRows = NEXT_BUCKETS.flatMap((k) => visible.filter((it) => it.bucket === k).sort(byDate));
  const nextShown = moreOpen ? nextRows : nextRows.slice(0, NEXT_SHOWN);
  const nextHidden = nextRows.length - nextShown.length;
  const generalAll = GENERAL_BUCKETS.flatMap((k) => visible.filter((it) => it.bucket === k));
  const generalUndone = generalAll.filter((it) => !isDone(it)).sort(byDate); // '9999' sinks the undated
  const generalRows = [...generalUndone, ...generalAll.filter(isDone)];
  const toggleTail = () => setTailOpen((v) => {
    try { localStorage.setItem(TAIL_KEY, v ? '0' : '1'); } catch {}
    return !v;
  });
  const pct = todayRows.length ? (todayDone.length / todayRows.length) * 100 : 0;
  let i = 0;
  const row = (it, inNext) => (
    <QueueItem key={it.id} item={it} index={i++} inNext={inNext} soloFilter={who.size === 1}
               suppressRef={suppressRef} onCtx={(x, y, item) => setCtx({ x, y, item })} />
  );

  return (
    <>
      {chips}
      {!items.length
        ? <div className="now-empty">nothing yet — add an action or habit to a mission</div>
        : (
          <>
            <div id="carried-banner-wrap" />
            <header className="now-day">
              <h1 className="now-day-date text-title">{dayLine()}</h1>
              <span className="now-day-count">{todayDone.length} / {todayRows.length}</span>
            </header>
            <div className="now-progress" aria-hidden="true"><div className="now-progress-fill" style={{ '--p': `${pct}%` }} /></div>

            <div className="queue-section now-today" data-bucket="today">
              <div className="queue-section-list">
                {todayOpen.map((it) => row(it, false))}
                {todayDone.map((it) => row(it, false))}
              </div>
              <BucketAdd bucketKey="today" who={who} onAdded={onAdded} />
              {todayRows.length > 0 && todayOpen.length === 0 && (
                <div className="now-clear">
                  <svg className="now-clear-mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" aria-hidden="true">
                    <path pathLength="1" d="M12 12L4 5" /><path pathLength="1" d="M12 12l8-7" /><path pathLength="1" d="M12 12l-7 8" />
                    <path pathLength="1" d="M12 12l7 8" /><path pathLength="1" d="M12 12H3" /><path pathLength="1" d="M12 12h9" />
                    <circle pathLength="1" cx="12" cy="12" r="1.6" />
                  </svg>
                  <div>
                    <div className="now-clear-line text-title">That's the day.</div>
                    <div className="now-clear-sub">every row ticked · the rest can wait for tomorrow</div>
                  </div>
                </div>
              )}
            </div>

            <div className="queue-section now-next" data-bucket="week">
              <div className="queue-section-head">Up next</div>
              <div className="queue-section-list">
                {nextShown.map((it) => row(it, true))}
                {!nextRows.length && <div className="queue-section-empty" />}
              </div>
              {(nextHidden > 0 || moreOpen) && (
                <button type="button" className="now-more" onClick={() => setMoreOpen((v) => !v)}>
                  <span className="now-more-plus">{moreOpen ? '−' : '+'}</span>
                  <span>{moreOpen ? 'show fewer' : `show ${nextHidden} more`}</span>
                </button>
              )}
              <BucketAdd bucketKey="week" who={who} onAdded={onAdded} />
            </div>

            <div className={`queue-section now-general${tailOpen ? ' open' : ''}`} data-bucket="general">
              {/* The head is the handle, and says how much is behind it. Shut,
                  it is one line — the page still answers "what do I do now?"
                  at a glance — and a row can still be dropped on it. */}
              <button type="button" className="queue-section-head now-general-head"
                      aria-expanded={tailOpen} onClick={toggleTail}>
                <span className="now-general-caret" aria-hidden="true">{tailOpen ? '−' : '+'}</span>
                In general
                {generalUndone.length > 0 && <span className="qs-count">{generalUndone.length}</span>}
              </button>
              {/* OPEN, THE BLOCK SCROLLS, IT DOES NOT PAGINATE: this is the
                  long tail — a hundred rows is normal — and "show 96 more" is
                  a button nobody presses. The list keeps its own scroll so the
                  day at the top of the page never leaves the screen. */}
              {tailOpen && (
                <>
                  <div className="queue-section-list now-general-list">
                    {generalRows.map((it) => row(it, true))}
                    {!generalRows.length && <div className="queue-section-empty" />}
                  </div>
                  <BucketAdd bucketKey="general" who={who} onAdded={onAdded} />
                </>
              )}
            </div>
          </>
        )}
      {ctx && <CtxMenu menu={ctx} onClose={() => setCtx(null)} />}
    </>
  );
}
